import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { Card } from './Card';
import { Skeleton } from './Feedback';

export function PageHeader({ title, subtitle, actions, crumbs }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; crumbs?: { label: string; to?: string }[] }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {crumbs && (
          <nav aria-label="Breadcrumb" className="mb-1 flex flex-wrap items-center gap-1 text-xs text-ink-subtle">
            {crumbs.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1">
                {c.to ? <Link to={c.to} className="hover:text-ink">{c.label}</Link> : <span>{c.label}</span>}
                {i < crumbs.length - 1 && <ChevronRight className="h-3 w-3" />}
              </span>
            ))}
          </nav>
        )}
        <h1 className="truncate text-xl font-semibold tracking-tight text-ink sm:text-2xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon, hint, tone = 'brand', loading, to }: { label: string; value: ReactNode; icon?: ReactNode; hint?: ReactNode; tone?: 'brand' | 'danger' | 'warning' | 'info' | 'success'; loading?: boolean; to?: string }) {
  const chip = { brand: 'bg-brand-soft text-brand-ink', danger: 'bg-danger-soft text-danger', warning: 'bg-warning-soft text-warning', info: 'bg-info-soft text-info', success: 'bg-success-soft text-success' }[tone];
  const content = (
    <Card className={cn('h-full p-4 transition-shadow', to && 'hover:shadow-md')}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-ink-muted">{label}</p>
        {icon && <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', chip)} aria-hidden>{icon}</span>}
      </div>
      {loading ? <Skeleton className="mt-2 h-7 w-20" /> : <p className="tabular mt-1 text-2xl font-semibold tracking-tight text-ink">{value}</p>}
      {hint && <p className="mt-1.5 text-xs text-ink-subtle">{hint}</p>}
    </Card>
  );
  return to ? <Link to={to} className="block rounded-xl">{content}</Link> : content;
}

export function Avatar({ name, size = 'md', className }: { name?: string | null; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const s = { sm: 'h-7 w-7 text-[11px]', md: 'h-9 w-9 text-xs', lg: 'h-14 w-14 text-base' }[size];
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full bg-brand-soft font-semibold text-brand-ink', s, className)} aria-hidden>
      {initials(name)}
    </span>
  );
}

export function Tabs<T extends string>({ value, onChange, items, className }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode; count?: number }[]; className?: string }) {
  return (
    <div role="tablist" className={cn('flex gap-1 overflow-x-auto border-b border-line', className)}>
      {items.map((it) => (
        <button
          key={it.value}
          role="tab"
          type="button"
          aria-selected={value === it.value}
          onClick={() => onChange(it.value)}
          className={cn(
            '-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
            value === it.value ? 'border-brand text-brand-ink' : 'border-transparent text-ink-muted hover:text-ink',
          )}
        >
          {it.label}
          {it.count !== undefined && <span className="tabular rounded bg-surface-hover px-1.5 text-xs text-ink-muted">{it.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function DescriptionList({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.map((it) => (
        <div key={it.label} className="min-w-0">
          <dt className="text-xs text-ink-subtle">{it.label}</dt>
          <dd className="mt-0.5 break-words text-sm text-ink">{it.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
